import { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
  maxItems?: number;
}

export function CategoryChipInput({
  value,
  onChange,
  suggestions = [],
  placeholder = 'Add category…',
  maxItems = 20,
}: Props) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);

  const valueSetLower = useMemo(
    () => new Set(value.map((v) => v.toLowerCase())),
    [value],
  );

  const filteredSuggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    return suggestions
      .filter((s) => !valueSetLower.has(s.toLowerCase()))
      .filter((s) => (q ? s.toLowerCase().includes(q) : true))
      .slice(0, 8);
  }, [input, suggestions, valueSetLower]);

  const addCategory = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    if (value.length >= maxItems) return;
    if (valueSetLower.has(trimmed.toLowerCase())) return;
    const exact = suggestions.find((s) => s.toLowerCase() === trimmed.toLowerCase());
    onChange([...value, exact ?? trimmed]);
    setInput('');
    setOpen(false);
  };

  const removeAt = (i: number) => {
    onChange(value.filter((_, idx) => idx !== i));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCategory(input);
    } else if (e.key === 'Backspace' && input === '' && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  const disabled = value.length >= maxItems;

  return (
    <div className="space-y-1.5">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((name, i) => (
            <Badge key={`${name}-${i}`} variant="secondary" className="gap-1 text-xs">
              {name}
              <button
                type="button"
                onClick={() => removeAt(i)}
                aria-label={`Remove ${name}`}
                className="ml-0.5 rounded hover:bg-muted-foreground/20"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <div className="relative">
        <Input
          value={input}
          disabled={disabled}
          placeholder={disabled ? `Maximum ${maxItems} categories` : placeholder}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            if (input.trim()) addCategory(input);
            setTimeout(() => setOpen(false), 150);
          }}
          onKeyDown={handleKeyDown}
        />
        {open && filteredSuggestions.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md">
            {filteredSuggestions.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addCategory(s);
                  }}
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent"
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
